/**
 * Is this a lost connection, or a bug?
 *
 * WHY THIS EXISTS
 * `fetch()` reports a dead network by throwing a **TypeError**. So when the wifi blinked, the
 * error page rendered its raw chain — `TypeError: Failed to fetch`, followed by a stack trace —
 * under the heading "Something went wrong", with a Restart button. Users read that as the app
 * crashing on a "typescript error" and restarted a perfectly healthy app. The one thing the page
 * did not say is the only thing that was true: the network dropped, and nothing is broken.
 *
 * WHY NOT `error instanceof TypeError`
 * `utils/server-health.ts` treats ANY TypeError as retryable, which is right for a retry loop —
 * retrying a real bug costs a few milliseconds. It is wrong here. A TypeError is overwhelmingly
 * a genuine defect (`undefined is not a function`), and telling someone "check your connection"
 * when their app is actually broken hides the bug and wastes their time. So a TypeError only
 * counts when its message is one the fetch implementations actually produce for a dead network.
 *
 * Each engine words it differently, and all of them are here on purpose: the user's browser is
 * whatever Electron ships, and a message this list misses lands them back on the raw stack.
 */

/** What fetch says when the network is gone. Chrome, Firefox, Safari, undici/Node, RN. */
const FETCH_FAILURE =
  /failed to fetch|networkerror when attempting to fetch|load failed|fetch failed|network request failed|connection (?:refused|reset|closed)|internet connection appears to be offline|network is unreachable/i

/** The codes the OS reports underneath, shared by both matchers below so they can never drift. */
const NETWORK_CODES =
  "ECONNREFUSED|ECONNRESET|ECONNABORTED|ENOTFOUND|ETIMEDOUT|EAI_AGAIN|EHOSTUNREACH|ENETUNREACH|ENETDOWN|EPIPE|UND_ERR_(?:CONNECT_TIMEOUT|SOCKET|HEADERS_TIMEOUT)"

/** What the OS says underneath, usually on `cause` rather than the message. */
const NETWORK_CODE = new RegExp(`^(?:${NETWORK_CODES})$`, "i")

/**
 * The same code, but buried inside someone else's sentence.
 *
 * Electron's IPC rewrites a rejection from the main process as
 * `Error invoking remote method 'control-plane-fetch': Error: read ECONNRESET`. That crosses the
 * boundary as a plain Error: `code` is gone, `cause` is gone, and the name is `Error`, so every
 * other check here misses it — the anchored matcher above because the code is no longer the whole
 * string, FETCH_FAILURE because "read ECONNRESET" is not how fetch words it. The message is the
 * only surviving evidence, so the code has to be readable where it actually ends up. Word-bounded
 * on purpose: it should match the code, not a longer identifier that merely contains it.
 */
const WRAPPED_NETWORK_CODE = new RegExp(`\\b(?:${NETWORK_CODES})\\b`, "i")

const hasCode = (value: unknown): value is { code: string } =>
  typeof value === "object" && value !== null && typeof (value as { code?: unknown }).code === "string"

/**
 * True when `error` is the network failing rather than the app failing.
 *
 * Walks `cause`, because undici surfaces the real reason there: the outer error is a bare
 * `TypeError: fetch failed` and the ECONNREFUSED that explains it is one level down.
 */
export function isNetworkError(error: unknown, depth = 0): boolean {
  // `cause` chains can loop; a handful of levels is far more than any real one needs.
  if (!error || depth > 5) return false

  if (typeof error === "string")
    return FETCH_FAILURE.test(error) || NETWORK_CODE.test(error.trim()) || WRAPPED_NETWORK_CODE.test(error)

  if (hasCode(error) && NETWORK_CODE.test(error.code.trim())) return true

  if (error instanceof Error) {
    // A network TypeError is identified by its MESSAGE, never by its type alone — see above.
    if (error.name === "TypeError" && FETCH_FAILURE.test(error.message)) return true
    // Non-TypeError errors that name a network failure outright (undici, node:http, our own).
    if (error.name !== "TypeError" && FETCH_FAILURE.test(error.message)) return true
    // A code that survived only as text, e.g. anything relayed across Electron's IPC boundary.
    if (WRAPPED_NETWORK_CODE.test(error.message)) return true
    if (error.cause) return isNetworkError(error.cause, depth + 1)
  }

  return false
}

/**
 * The desktop main process telling us it could not REACH the sign-in service.
 *
 * Matched by message on purpose: custom Error properties do not survive Electron's IPC boundary
 * (see `openAnalystApiSessionUnavailableMessage` in desktop/src/main/auth.ts), so the message is
 * the only channel the meaning can travel through. Keep the two in step.
 */
const SESSION_UNAVAILABLE = /could not reach openanalyst to confirm your session/i
const DESKTOP_AUTH_BROKER_TIMEOUT = /desktop token refresh timed out/i

/**
 * Is this failure one we know nothing from — as opposed to being told the session is invalid?
 *
 * The rule the whole auth path turns on: **evidence, not silence, ends a session.** A 401 is
 * evidence. A dropped connection, a 503, a timeout — those tell us nothing about the credentials,
 * and clearing tokens over them logs out a user whose session was perfectly good. Worse, on
 * desktop it deletes the refresh token, so the loss is permanent.
 */
export function isTransientAuthError(error: unknown): boolean {
  if (isNetworkError(error)) return true
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : ""
  return SESSION_UNAVAILABLE.test(message) || DESKTOP_AUTH_BROKER_TIMEOUT.test(message)
}
